import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { EventBusy } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Country, TechSetupFailureReason } from '../../../../../constants'
import { useAppointment, useCountry, useFeatureFlags } from '../../../../../hooks'
import { AppointmentCancellationFormData } from '../../../../../types'
import { sortByName } from '../../../../../util'
import { AppointmentInfo } from '../../../../AppointmentInfo'
import { CustomerName } from '../../../../CustomerName'
import { Loader, Container as LoaderContainer } from '../../../../Loader'
import { Spacer } from '../../../../Spacer'
import { Subtitle } from '../../../../Subtitle'
import { Title } from '../../../../Title'
import { Select as FormSelect, SubmitButton, TextArea } from '../../../../forms'
import { CallToActions } from '../../../../schedule'
import {
  getPrimaryCancellationReasonDisplayText,
  getPrimaryCancellationReasons,
  PrimaryAppointmentCancellationReason,
  techSetupReasonDisplayText,
} from '../../utils'
import { useTranslation } from 'react-i18next'

const Select = styled(FormSelect)`
  max-width: 50%;
`

const sortByValueWithOtherLast = (a: { value: string }, b: { value: string }): number => {
  if (a.value === PrimaryAppointmentCancellationReason.OTHER) return 1
  if (b.value === PrimaryAppointmentCancellationReason.OTHER) return -1

  return a.value.localeCompare(b.value)
}

const techSetupFailureReasons = Object.values(TechSetupFailureReason)
  .map((reason) => ({
    name: techSetupReasonDisplayText[reason],
    value: reason,
  }))
  .sort(sortByName)

interface ContentProps {
  deliveryModel?: DeliveryModel
}

const Content = ({ deliveryModel }: ContentProps) => {
  const { t } = useTranslation()
  const reason = useWatch<AppointmentCancellationFormData>({
    name: 'reason',
  })
  const country = useCountry()

  const [isTechSetupFailReasonsEnabledFeatureFlag] = useFeatureFlags<boolean>(['DISPLAY_TECH_SETUP_REASONS'])

  const reasons = useMemo(() => {
    const displayText = getPrimaryCancellationReasonDisplayText(country, deliveryModel)
    const values = getPrimaryCancellationReasons(country, deliveryModel)
    let result = values.map((value) => ({ name: displayText[value], value }))
    if (country !== Country.DE) {
      result = result.sort(sortByValueWithOtherLast)
      if (!isTechSetupFailReasonsEnabledFeatureFlag) {
        result = result.filter((opt) => opt.value !== PrimaryAppointmentCancellationReason.TECH_SETUP_FAILED)
      }
    }
    return result
  }, [country, deliveryModel, isTechSetupFailReasonsEnabledFeatureFlag])

  const { formState } = useFormContext<AppointmentCancellationFormData>()
  const { isLoading: isAppointmentLoading } = useAppointment()

  const { isSubmitting } = formState
  const isLoading = isAppointmentLoading || isSubmitting
  const isNoteRequired = reason === PrimaryAppointmentCancellationReason.OTHER
  const isPrimaryReasonTechSetup = reason === PrimaryAppointmentCancellationReason.TECH_SETUP_FAILED
  const notePlaceholderText = isNoteRequired ? t('common.message.providerMoreInformation') : t('common.message.optionalMoreInformation')

  return (
    <>
      {isLoading && (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      )}
      <Title>{t('common.label.cancelAppointment')}</Title>
      <Spacer count={0.5} />
      <Subtitle>{t('pages.appointment.cancel.selectReasonForCancelling')}</Subtitle>
      <Spacer />
      <CustomerName />
      <Spacer count={0.25} />
      <AppointmentInfo />
      <Spacer count={1.5} />
      <Select
        defaultValue=""
        disabled={isLoading}
        label={t('pages.appointment.cancel.reasonForCancelling')}
        name="reason"
        options={reasons}
      />
      {isPrimaryReasonTechSetup && (
        <>
          <br />
          <Select
            defaultValue=""
            disabled={isLoading}
            label={t('common.label.reasonForUnsuccessfulTechSetup')}
            name="techSetupFailReason"
            options={techSetupFailureReasons}
          />
        </>
      )}
      <Spacer count={2.5} />
      {country !== Country.DE && (
        <TextArea
          disabled={isLoading}
          fullWidth
          InputLabelProps={{ shrink: true }}
          label={t('common.label.additionalInformation')}
          multiline
          name="description"
          placeholder={notePlaceholderText}
          rows={4}
          variant="outlined"
        />
      )}
      <Spacer count={2} />
      <CallToActions>
        <SubmitButton color="error" startIcon={<EventBusy />}>
          {t('common.label.cancelAppointment')}
        </SubmitButton>
      </CallToActions>
    </>
  )
}

export { Content }
